import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY = { name: "", service_id: "none", lesson_count: 10, price: 0, validity_days: 90, active: true };
const fmt = (n) => new Intl.NumberFormat("tr-TR").format(n);

export default function Packages() {
  const [items, setItems] = useState(null);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/packages").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/services").then((r) => setServices(r.data)); api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, service_id: p.service_id || "none", lesson_count: p.lesson_count, price: p.price, validity_days: p.validity_days, active: p.active }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, service_id: form.service_id === "none" ? null : form.service_id, lesson_count: Number(form.lesson_count), price: Number(form.price), validity_days: Number(form.validity_days), active: form.active, branch_id: (editing?.branch_id) || branches[0]?.id };
      if (editing) { await api.put(`/packages/${editing.id}`, payload); toast.success("Paket güncellendi"); }
      else { await api.post("/packages", payload); toast.success("Paket oluşturuldu"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/packages/${delId}`); toast.success("Paket silindi"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDelId(null); }
  };

  return (
    <div data-testid="admin-packages-page">
      <PageHeader title="Paketler" subtitle={`${items?.length ?? "—"} paket`}
        action={<Button onClick={openNew} data-testid="add-new-package-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Paket</Button>} />

      {items === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl bg-[#13131C]" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`package-card-${p.id}`} className="rounded-2xl bg-[#13131C] border border-[#222230] p-5 hover:border-[#FF5500]/40 transition-all group flex flex-col">
              <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md bg-[#FF5500]/10 text-[#FF5500] border border-[#FF5500]/30">{p.service_name}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(p)} data-testid={`edit-package-${p.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#252536]"><Pencil size={15} /></button>
                  <button onClick={() => setDelId(p.id)} data-testid={`delete-package-${p.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#EF4444] hover:bg-[#252536]"><Trash2 size={15} /></button>
                </div>
              </div>
              <h3 className="font-display font-bold text-white text-lg mt-3">{p.name}</h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-mono font-extrabold text-2xl text-white">{p.lesson_count}</span>
                <span className="text-sm text-[#68687B]">ders · {p.validity_days} gün geçerli</span>
              </div>
              <div className="mt-auto pt-4 border-t border-[#1A1A24] flex items-center justify-between">
                <span className="font-mono font-bold text-xl text-[#FF5500]">₺{fmt(p.price)}</span>
                <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border ${p.active ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" : "bg-[#68687B]/15 text-[#A0A0B2] border-[#68687B]/30"}`}>{p.active ? "Aktif" : "Pasif"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Paket Düzenle" : "Yeni Paket"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Paket Adı</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="package-form-name" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Hizmet</label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="package-form-service" className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue placeholder="Hizmet seç" /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">
                  <SelectItem value="none">Bağımsız</SelectItem>
                  {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Ders</label><Input type="number" value={form.lesson_count} onChange={(e) => setForm({ ...form, lesson_count: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Fiyat ₺</label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Gün</label><Input type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[#0E0E14] border border-[#262636] px-4 py-3">
              <span className="text-sm text-white">Aktif</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="package-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="package-form-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="bg-[#13131C] border-[#222230] text-white">
          <AlertDialogHeader><AlertDialogTitle>Paketi sil?</AlertDialogTitle><AlertDialogDescription className="text-[#A0A0B2]">Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1B1B27] border-[#2D2D3F] text-white hover:bg-[#252536]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-package" className="bg-[#EF4444] hover:bg-[#dc2626] text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
